import { DsButton } from '@frontends/ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { showToast } from '@/components/Toast.tsx';
import useAuth from '@/hooks/useAuth.tsx';
import useAxiosInterceptors from '@/hooks/useAxiosInterceptors.tsx';
import { useCompleteLogin } from '@/hooks/useCompleteLogin.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import { useNavigateRedirect } from '@/hooks/useNavigateRedirect.tsx';
import axios from '@/lib/axios.ts';
import { loginSchema, LoginSchemaType } from '@/types/Auth.ts';
import { type SessionResponse } from '@/types/Auth.ts';
import { IGenericError } from '@/types/Error.ts';
import {
  isAllowedOAuthReturnUrl,
  isEmbedded,
  notifyEmbedderLoginSuccess,
  readEmbedEntryParams,
} from '@/utils/embedAuth.ts';
import FormTextField from '../../components/FormTextField.tsx';
import Logo from '../../components/Logo.tsx';
import AuthFormWrapper from './AuthFormWrapper.tsx';

const Login = memo(function Login() {
  useAxiosInterceptors();
  const { t } = useTranslation();
  const { setToken, setClient } = useAuth();
  const completeLogin = useCompleteLogin();
  const [isView, setIsView] = useState(false);
  const [isB2CoreLoginLoading, setIsB2CoreLoginLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const config = useEntityConfig();
  const { handleNavigateRedirect } = useNavigateRedirect();
  const loginInFlightRef = useRef(false);
  const b2CoreLoginInFlightRef = useRef(false);

  let setReg: string | boolean = '';

  if (config.VITE_SET_REGISTRATION_LINK) {
    setReg = config.VITE_SET_REGISTRATION_LINK;
  } else {
    setReg = searchParams.get('setRegistrationLink') || '';
  }

  const loginForm = useForm<LoginSchemaType>({
    criteriaMode: 'all',
    resolver: zodResolver(loginSchema(t)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const getLoginErrorDescription = (e: AxiosError<IGenericError>): string => {
    if (e?.response?.data.context?.translationKey ?? '') {
      return t(e?.response?.data.context?.translationKey, {
        ttlInMinutes: e?.response?.data.context?.ttlInMinutes,
        ttlInSeconds: e?.response?.data.context?.ttlInSeconds,
      });
    }

    if (e?.response?.status === 429) {
      return t('too_many_login_attempts');
    }

    return t(e?.response?.data?.detail ?? e.message);
  };

  const loginApi = useCallback(async (data: LoginSchemaType) => {
    const response = await axios.post<SessionResponse>('/auth/login', data, {
      withCredentials: true,
      headers: { 'X-Trace-ID': uuidv4() },
    });

    return response.data;
  }, []);

  const loginMutation = useMutation({
    mutationKey: ['login'],
    mutationFn: loginApi,
    onSuccess: (data) => {
      const accessToken = data.access_token;

      if (!accessToken) {
        loginInFlightRef.current = false;
        setToken(undefined);
        setClient(undefined);
        showToast({
          type: 'error',
          title: t('error_title'),
          description: t('failed_to_create_session'),
        });

        return;
      }

      if (handleNavigateRedirect()) {
        return;
      }

      notifyEmbedderLoginSuccess();
      void completeLogin(accessToken);
    },
    onError: (e: AxiosError<IGenericError>) => {
      loginInFlightRef.current = false;
      setToken(undefined);
      setClient(undefined);
      showToast({
        type: 'error',
        title: t('error_title'),
        description: getLoginErrorDescription(e),
      });
    },
  });

  const b2CoreLoginApi = useCallback(async () => {
    const response = await axios.get<{ url: string }>('/oauth/b2core/login', {
      withCredentials: true,
      headers: { 'X-Trace-ID': uuidv4() },
    });

    return response.data;
  }, []);

  const b2CoreLoginMutation = useMutation({
    mutationKey: ['b2core-login'],
    mutationFn: b2CoreLoginApi,
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
    onError: (e: AxiosError<IGenericError>) => {
      b2CoreLoginInFlightRef.current = false;
      setIsB2CoreLoginLoading(false);
      showToast({
        type: 'error',
        title: t('error_title'),
        description: getLoginErrorDescription(e),
      });
    },
  });

  const loginOnSubmit = useCallback(
    (data: LoginSchemaType, e?: React.BaseSyntheticEvent) => {
      e?.preventDefault();

      if (loginInFlightRef.current) {
        return;
      }

      loginInFlightRef.current = true;
      loginMutation.mutate(data);
    },
    [loginMutation],
  );

  const handleB2CoreLogin = useCallback(() => {
    if (b2CoreLoginInFlightRef.current || b2CoreLoginMutation.isPending) {
      return;
    }

    b2CoreLoginInFlightRef.current = true;
    setIsB2CoreLoginLoading(true);

    if (isEmbedded()) {
      const embedParams = readEmbedEntryParams(searchParams);
      const requestedReturnTo = embedParams.get('return_to');
      const returnTo =
        requestedReturnTo &&
        isAllowedOAuthReturnUrl(requestedReturnTo, [config.VITE_TRADING_VIEW_URL, config.VITE_TRADING_VIEW_REPLAY_URL])
          ? requestedReturnTo
          : config.VITE_TRADING_VIEW_URL;
      const startUrl = new URL('/oauth/start', window.location.origin);
      startUrl.searchParams.set('return_to', returnTo);

      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- same-origin top window is always present in this app context
        window.top!.location.href = startUrl.toString();
      } catch {
        b2CoreLoginInFlightRef.current = false;
        setIsB2CoreLoginLoading(false);
        showToast({
          type: 'error',
          title: t('error_title'),
          description: t('oauth_login_failed'),
        });
      }

      return;
    }

    b2CoreLoginMutation.mutate();
  }, [b2CoreLoginMutation, config.VITE_TRADING_VIEW_REPLAY_URL, config.VITE_TRADING_VIEW_URL, searchParams, t]);

  useEffect(() => {
    const resetB2CoreButton = () => {
      b2CoreLoginInFlightRef.current = false;
      setIsB2CoreLoginLoading(false);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetB2CoreButton();
      }
    };
    window.addEventListener('pageshow', resetB2CoreButton);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', resetB2CoreButton);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const NoAccountText = useMemo(() => {
    if (setReg === 'false' || !setReg) return null;

    let DynamicLink = (
      <Link to={{ pathname: '/register' }} className="hover:text-textBrand underline underline-offset-4" />
    );

    if (setReg && typeof setReg === 'string') {
      DynamicLink = (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a
          href={setReg}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-textBrand underline underline-offset-4"
        />
      );
    }

    return (
      <Trans
        i18nKey="no_account"
        t={t}
        components={{
          register: DynamicLink,
        }}
      />
    );
  }, [setReg, t]);

  return (
    <AuthFormWrapper>
      <div className="flex w-full items-center justify-center">
        <div className="flex flex-col justify-center min-h-[220px] gap-6 w-full max-w-[348px] rounded-2xl border border-borderSurface bg-backgroundRaised py-6 text-textPrimary shadow-sm lg:border-none lg:shadow-none">
          <div className="flex justify-center items-center px-6">
            <Logo className="[zoom:1.25]" />
          </div>

          {config.VITE_B2CORE_OAUTH_ENABLED ? (
            <div className="px-6">
              <div className="*:sm:w-[300px]">
                <DsButton
                  size="xl"
                  className="w-full mt-3"
                  isPending={isB2CoreLoginLoading}
                  onPress={handleB2CoreLogin}
                >
                  {t('login')}
                </DsButton>
              </div>
            </div>
          ) : (
            <div className="px-6">
              <form onSubmit={loginForm.handleSubmit(loginOnSubmit)}>
                <div className="flex flex-col gap-6 *:sm:w-[300px]">
                  <div className="flex gap-2">
                    <FormTextField
                      control={loginForm.control}
                      name="email"
                      id="email"
                      type="email"
                      label={t('email')}
                      placeholder="email@example.com"
                      isRequired
                    />
                  </div>
                  <div className="flex gap-2">
                    <FormTextField
                      control={loginForm.control}
                      name="password"
                      id="password"
                      type={isView ? 'text' : 'password'}
                      label={t('password')}
                      placeholder="Password"
                      isRequired
                      buttonEnd={{
                        icon: isView ? 'eye' : 'eye-off',
                        'aria-label': t(isView ? 'hide_password' : 'show_password'),
                        onPress: () => setIsView(!isView),
                      }}
                    />
                  </div>
                  <DsButton
                    type="submit"
                    size="lg"
                    className="w-full"
                    isPending={loginForm.formState.isSubmitting || loginMutation.isPending || loginMutation.isSuccess}
                  >
                    {t('login')}
                  </DsButton>
                  {NoAccountText && (
                    <div className="flex gap-2 text-center text-base justify-center">
                      <span>{NoAccountText}</span>
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </AuthFormWrapper>
  );
});

export default Login;
